import React, { useState } from "react"
import { BookOpenIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import useT from "../hooks/useT"
import FlipBook from "./FlipBook"
import Img from "./Img"
import Chip from "./Chip"
import ProductPurchaseUI from "./ProductPurchaseUI"
import { displayName } from "../utils/productUtils"
import { useProfileName } from "../hooks/useCatalogData"
import { PLACEMENTS } from "../store/placements"

// The header of the full-screen viewer: the product's name as its title, and
// a round close button on the right that is plain to see and easy to hit
// (the variant's bare X in the corner was easy to miss over a white page).
const VIEWER = {
  container: "w-screen h-[100dvh] max-w-none bg-dark border-0 rounded-none shadow-none overflow-hidden flex flex-col p-0 m-0",
  header: "relative flex items-center h-[60px] pl-4 pr-[68px] shrink-0 border-b border-white/10",
  title: "flex-1 min-w-0 m-0 text-[17px] md:text-[19px] font-bold text-white truncate",
  closeButton: "absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors",
  closeIcon: "h-6 w-6 text-white",
  headerContent: "shrink-0 ml-3",
  contentWrapper: "flex-1 min-h-0 overflow-hidden",
  content: "h-full min-h-0 p-0",
}

// "Look inside" — the product's preview file (a PDF of sample pages, an
// absolute url the API answers as `preview`) in the aldoo-ra full-screen
// modal, as a book whose pages turn (./FlipBook), under a header with the
// title and a close. Escape closes it too. Client-only, as the trailer is.
// the book's sounds, off or on, as the reader left them (this browser only)
const MUTED = "aldoo-flipbook-muted"
const readMuted = () => {
  try {
    return window.localStorage.getItem(MUTED) === "1"
  } catch {
    return false
  }
}

/**
 * The player's buy bar, as the gallery's lightbox has it (ProductGallery): the
 * thumbnail, the name and kind (not on a phone: no room), the buy button.
 */
function PurchaseBar({ product }) {
  const contentTypeLabel = useProfileName(product.profile)
  return (
    <div className="shrink-0 w-full bg-paper text-text px-4 tab:px-[50px] md:px-[80px] py-3 flex items-center gap-2 tab:gap-4" data-testid="look-inside-purchase-bar">
      <div className="w-10 h-10 tab:w-[50px] tab:h-[50px] md:w-[60px] md:h-[60px] shrink-0 rounded overflow-hidden">
        <Img src={product.square || product.thumb} alt={displayName(product)} className="w-full h-full" />
      </div>
      <div className="hidden md:flex flex-1 min-w-0 flex-col items-start gap-1">
        <div className="w-full truncate text-[16px] md:text-[18px] font-semibold">{displayName(product)}</div>
        {contentTypeLabel && <Chip tone="outline" size="sm" label={contentTypeLabel} to={`/type/${product.profile?.id}`} />}
      </div>
      <div className="flex-1 md:hidden" />
      <div className="shrink-0">
        <ProductPurchaseUI product={product} compact placement={PLACEMENTS.LOOK_INSIDE} />
      </div>
    </div>
  )
}

/**
 * The book player, full screen: `url` (the preview PDF) as a book whose pages
 * turn, under a header with `title`, the sound switch and a close. With a
 * `product`, the buy bar under the header (the store's "Look inside"; the
 * product editor's Book preview, Store/extensions/book-preview, has none).
 */
export function LookInsideViewer({ url, title = "", product = null, onClose }) {
  const t = useT()
  const [muted, setMuted] = useState(readMuted)
  const toggleMuted = () => {
    const next = !muted
    setMuted(next)
    try {
      window.localStorage.setItem(MUTED, next ? "1" : "0")
    } catch {
      // storage refused: muted for this visit
    }
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="fullscreen"
      title={title}
      closeOnEscape
      styleOverrides={VIEWER}
      headerContent={
        <button
          type="button"
          onClick={toggleMuted}
          aria-pressed={muted}
          aria-label={muted ? t`Turn the sound on` : t`Mute the sound`}
          title={muted ? t`Turn the sound on` : t`Mute the sound`}
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="look-inside-mute"
        >
          {muted ? <SpeakerXMarkIcon className="h-6 w-6 text-white" /> : <SpeakerWaveIcon className="h-6 w-6 text-white" />}
        </button>
      }
    >
      <div className="w-full h-full flex flex-col" data-testid="look-inside-viewer">
        {product && <PurchaseBar product={product} />}
        <div className="flex-1 min-h-0">
          <FlipBook url={url} title={title} muted={muted} />
        </div>
      </div>
    </Modal>
  )
}

export default function LookInsideButton({ product, className = "" }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const url = product?.preview

  if (!url) return null
  return (
    <>
      <Button variant="secondary" className={className} onClick={() => setOpen(true)} data-testid="look-inside">
        <BookOpenIcon className="w-4 h-4 mr-1" />
        {t`Look inside`}
      </Button>
      {open && <LookInsideViewer url={url} title={product.name} product={product} onClose={() => setOpen(false)} />}
    </>
  )
}
